import { addHours } from 'date-fns/addHours';
import { formatISO } from 'date-fns/formatISO';
import { isBefore } from 'date-fns/isBefore';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function getVersionInfo(userAgent, keyList, separator) {
    var versionInfo = userAgent.split(' ').find(function (userAgentString) {
        var matched = keyList.find(function (key) { return userAgentString.includes(key); });
        return Boolean(matched);
    });
    if (!versionInfo) {
        return null;
    }
    var _a = __read(versionInfo.split("/".concat(separator, ";")), 2), platform = _a[0], version = _a[1];
    return [platform, version];
}
/**
 * Detect mobile platform and app version
 * @param {string} userAgent
 * @param {'traveler' | 'guide'} appType - application type (마이리얼트립 앱, 마이리얼트립 가이드 앱)
 * @returns {string[] | undefined}
 */
function getMobileAppVersion(_a) {
    var userAgent = _a.userAgent, appType = _a.appType;
    if (!userAgent)
        return;
    var androidPlatformKey = "android/".concat(appType, ";");
    var iosPlatformKey = "ios/".concat(appType, ";");
    var versionInfo = getVersionInfo(userAgent, [androidPlatformKey, iosPlatformKey], appType);
    if (!versionInfo) {
        return;
    }
    return versionInfo;
}

function createLocalStorageChangeEvent(payload) {
    return new CustomEvent('onLocalStorageChange', { detail: payload });
}
createLocalStorageChangeEvent.eventName = 'onLocalStorageChange';

function canUseStorage() {
    var storage = globalThis.localStorage;
    if (storage == null) {
        return false;
    }
    try {
        var testItem = '__storage_test__';
        storage.setItem(testItem, testItem);
        storage.removeItem(testItem);
        return true;
    }
    catch (e) {
        return (e instanceof DOMException &&
            (e.code === 22 ||
                e.code === 1014 ||
                e.name === 'QuotaExceededError' ||
                e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            storage &&
            storage.length > 0);
    }
}
var LocalStorage = /** @class */ (function () {
    function LocalStorage() {
    }
    LocalStorage.prototype.getItem = function (key) {
        return localStorage.getItem(key);
    };
    LocalStorage.prototype.setItem = function (key, value) {
        localStorage.setItem(key, value);
    };
    LocalStorage.prototype.removeItem = function (key) {
        localStorage.removeItem(key);
    };
    LocalStorage.prototype.clear = function () {
        localStorage.clear();
    };
    return LocalStorage;
}());
var MemoryStorage = /** @class */ (function () {
    function MemoryStorage() {
        this.memoryStorage = new Map();
    }
    MemoryStorage.prototype.getItem = function (key) {
        var _a;
        return (_a = this.memoryStorage.get(key)) !== null && _a !== void 0 ? _a : null;
    };
    MemoryStorage.prototype.setItem = function (key, value) {
        this.memoryStorage.set(key, value);
    };
    MemoryStorage.prototype.removeItem = function (key) {
        this.memoryStorage.delete(key);
    };
    MemoryStorage.prototype.clear = function () {
        this.memoryStorage.clear();
    };
    return MemoryStorage;
}());
var storageFrom = function (isAvailable) {
    return isAvailable ? new LocalStorage() : new MemoryStorage();
};
var storage = storageFrom(canUseStorage());

function getExpiry(expiryHour) {
    var now = new Date();
    if (expiryHour != null) {
        return formatISO(addHours(now, expiryHour));
    }
    else {
        return '';
    }
}
function isMyrealtripStorageItem(item) {
    return 'data' in item && 'expiry' in item;
}
/**
 * 로컬스토리지에 데이터를 저장합니다. 3번째 인자 expiryHour로 데이터의 만료 시간을 지정할 수 있습니다.
 * @template T
 * @param {string} key - 값이 저장될 키입니다.
 * @param {T} data - 저장할 값입니다.
 * @param {number} [expiryHour] - 값이 유효한 시간(시간)입니다.
 * @example
 * ```ts
 * setLocalStorageItem('username', 'myusername', 24); // username이라는 키로 myusername 값을 24시간 동안 로컬 스토리지에 저장합니다.
 * ```
 */
function setLocalStorageItem(key, data, expiryHour) {
    var payload = {
        data: data,
        expiry: getExpiry(expiryHour),
    };
    storage.setItem(key, JSON.stringify(payload));
    globalThis.dispatchEvent(createLocalStorageChangeEvent({ key: key, data: data }));
}
/**
 * 로컬스토리지에서 데이터를 가져옵니다. 만약 만료 시간이 지정된 데이터이고, 만료 시간이 지난 상태라면 null이 반환됩니다.
 * @template T
 * @param {string} key 가져올 localStorage 아이템의 키
 * @returns {T | null} 가져온 아이템의 값, 만료된 아이템이거나 존재하지 않는다면 null
 * @example
 * ```ts
 * const username = getLocalStorageItem('username'); // username이라는 키로 저장된 값을 가져옵니다.
 * ```
 */
function getLocalStorageItem(key) {
    var payload = storage.getItem(key);
    if (payload == null) {
        return null;
    }
    var parsedPayload = payload !== '' ? JSON.parse(payload) : payload;
    if (isMyrealtripStorageItem(parsedPayload)) {
        var now = new Date();
        var expiry = new Date(parsedPayload.expiry);
        if (isNaN(expiry.getTime()) || isBefore(now, expiry)) {
            return parsedPayload.data;
        }
        else {
            storage.removeItem(key);
            return null;
        }
    }
    else {
        return parsedPayload;
    }
}
/**
 * 로컬스토리지에서 데이터를 제거합니다.
 * @param {string} key 제거할 localStorage 아이템의 키
 * @example
 * ```ts
 * removeLocalStorageItem('username'); // username이라는 키로 저장된 값을 제거합니다.
 * ```
 */
function removeLocalStorageItem(key) {
    storage.removeItem(key);
    globalThis.dispatchEvent(createLocalStorageChangeEvent({ key: key, data: null }));
}
/**
 * 로컬스토리지에서 데이터를 가져온 후 해당 데이터를 로컬스토리지에서 제거합니다.
 * @template T
 * @param {string} key 가져올 localStorage 아이템의 키
 * @returns {T | null} 가져온 아이템의 값, 만료된 아이템이거나 존재하지 않는다면 null
 * @example
 * ```ts
 * const username = popLocalStorageItem('username'); // username이라는 키로 저장된 값을 가져온 후 제거합니다.
 * ```
 */
function popLocalStorageItem(key) {
    var data = getLocalStorageItem(key);
    removeLocalStorageItem(key);
    globalThis.dispatchEvent(createLocalStorageChangeEvent({ key: key, data: null }));
    return data;
}
/**
 * 로컬스토리지 내의 모든 데이터를 제거합니다.
 * @example
 * ```ts
 * clearLocalStorage(); // 로컬스토리지 내의 모든 데이터를 제거합니다.
 * ```
 */
function clearLocalStorage() {
    storage.clear();
}

/**
 * Move scroll to the top of element
 * @param {HTMLElement} element
 * @param {number} [offset=0] - scroll with space on top
 */
var scrollWidthOffset = function (element, offset) {
    if (offset === void 0) { offset = 0; }
    var elementPosition = element.offsetTop - offset;
    window.scroll({
        top: elementPosition,
        left: 0,
        behavior: 'smooth',
    });
};

export { clearLocalStorage, createLocalStorageChangeEvent, getLocalStorageItem, getMobileAppVersion, isMyrealtripStorageItem, popLocalStorageItem, removeLocalStorageItem, scrollWidthOffset, setLocalStorageItem };
